import React, { useEffect, useState, useMemo, useCallback } from "react";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";
import { Users, Sparkles, Gift, Crown, Search, ArrowUpDown, ShieldAlert, Radio, Loader2, Unlock, Lock } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import api from "@/lib/api";
import { fmtNum, fmtFecha, fmtFechaCorta, fmtDuracion } from "@/lib/format";
import { toast } from "sonner";

const SERIES = [
  { key: "total", label: "Total", color: "#D4AF37" },
  { key: "trial", label: "Trial", color: "#4ECDC4" },
  { key: "free", label: "Free", color: "#A1A1AA" },
  { key: "premium", label: "Premium", color: "#FFD700" },
];

// Tiempo de uso diario promedio (minutos) — lo provee el puente de Axiora.
const usoMin = (u) =>
  u.uso_promedio_diario_min ?? u.tiempo_uso_diario_prom_min ?? u.uso_diario_promedio_min ??
  u.minutos_uso_diario ?? u.tiempo_uso_min ?? u.uso_min ?? null;
const usoHoyMin = (u) => u.uso_hoy_min ?? null;

const StatCard = ({ icon: Icon, label, value, color, testid }) => (
  <div data-testid={testid} className="rounded-xl bg-[#0A0A0A] border border-[#1F1F24] p-5 flex flex-col gap-3 hover:border-[#D4AF37]/30 transition-colors">
    <div className="flex items-center justify-between">
      <span className="text-[11px] text-[#71717A] uppercase tracking-[0.2em]">{label}</span>
      <Icon className="w-4 h-4" style={{ color }} />
    </div>
    <span className="font-mono-ax text-4xl" style={{ color }}>{fmtNum(value)}</span>
  </div>
);

const Usuarios = () => {
  const [resumen, setResumen] = useState(null);
  const [lista, setLista] = useState(null);
  const [hidden, setHidden] = useState({});
  const [q, setQ] = useState("");
  const [sortKey, setSortKey] = useState("prioridad");
  const [sortDir, setSortDir] = useState("desc");
  const [expUser, setExpUser] = useState(null);
  const [expData, setExpData] = useState(null);
  const [expLoading, setExpLoading] = useState(false);
  const [acting, setActing] = useState(false);

  const cargar = useCallback(async () => {
    try {
      const [r, l] = await Promise.all([
        api.get("/comando/usuarios/resumen"),
        api.get("/comando/usuarios/lista"),
      ]);
      setResumen(r.data);
      setLista(l.data);
    } catch (e) { /* noop */ }
  }, []);

  useEffect(() => {
    document.title = "Usuarios · AXIORA";
    cargar();
    const t = setInterval(cargar, 30000);
    return () => clearInterval(t);
  }, [cargar]);

  const pendienteR = resumen?.puente_pendiente;
  const pendienteL = lista?.puente_pendiente;
  const ahora = resumen?.ahora || { total: 0, trial: 0, free: 0, premium: 0 };
  const serie = resumen?.serie_diaria || [];

  const modoMeses = serie.length > 30;
  const chartData = useMemo(() => {
    if (!modoMeses) {
      return serie.map((s) => ({ x: fmtFechaCorta(s.fecha).replace(/ de| \d{4}/g, ""), total: s.total, trial: s.trial, free: s.free, premium: s.premium }));
    }
    const byMonth = {};
    serie.forEach((s) => {
      const d = new Date(s.fecha);
      const k = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
      byMonth[k] = { x: k, total: s.total, trial: s.trial, free: s.free, premium: s.premium };
    });
    return Object.values(byMonth);
  }, [serie, modoMeses]);

  const usuarios = lista?.usuarios || [];
  const restringidos = usuarios.filter((u) => u.restringido);

  const filtrados = useMemo(() => {
    let arr = usuarios.filter((u) => !u.restringido);
    if (q.trim()) {
      const s = q.toLowerCase();
      arr = arr.filter((u) => (u.email || "").toLowerCase().includes(s) || (u.plan || "").toLowerCase().includes(s) || (u.user_id || "").toLowerCase().includes(s));
    }
    arr = [...arr].sort((a, b) => {
      if (sortKey === "prioridad") {
        // Orden automático en vivo: Premium → más comb. guardadas → más boletos guardados
        const pa = a.plan === "premium" ? 0 : 1, pb = b.plan === "premium" ? 0 : 1;
        if (pa !== pb) return pa - pb;
        const ga = a.combinaciones_guardadas || 0, gb = b.combinaciones_guardadas || 0;
        if (gb !== ga) return gb - ga;
        return (b.boletos_total || 0) - (a.boletos_total || 0);
      }
      if (sortKey === "uso_min") {
        const va = usoMin(a) ?? -1, vb = usoMin(b) ?? -1;
        return sortDir === "asc" ? va - vb : vb - va;
      }
      let va = a[sortKey], vb = b[sortKey];
      if (sortKey === "registrado") { va = new Date(va).getTime(); vb = new Date(vb).getTime(); }
      if (va < vb) return sortDir === "asc" ? -1 : 1;
      if (va > vb) return sortDir === "asc" ? 1 : -1;
      return 0;
    });
    return arr;
  }, [usuarios, q, sortKey, sortDir]);

  const toggleSort = (k) => {
    if (sortKey === k) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else { setSortKey(k); setSortDir("desc"); }
  };

  const abrirExpediente = async (u) => {
    setExpUser(u); setExpData(null); setExpLoading(true);
    try {
      const { data } = await api.get(`/comando/usuarios/${u.user_id}/expediente`);
      setExpData(data);
    } catch (e) { setExpData({ error: true }); }
    finally { setExpLoading(false); }
  };

  const accion = async (tipo) => {
    if (!expUser) return;
    setActing(true);
    try {
      await api.post(`/comando/usuarios/${expUser.user_id}/${tipo === "desbloquear" ? "desbloquear" : "bloquear"}`);
      toast.success(tipo === "desbloquear" ? "Usuario desbloqueado" : "Bloqueo mantenido");
      setExpUser(null);
      cargar();
    } catch (e) {
      toast.error("No se pudo completar la acción");
    } finally { setActing(false); }
  };

  const PlanBadge = ({ plan }) => {
    const map = { premium: "#FFD700", trial: "#4ECDC4", free: "#A1A1AA" };
    return <span className="px-2 py-0.5 rounded-full text-[10px] uppercase tracking-wider border" style={{ color: map[plan] || "#A1A1AA", borderColor: (map[plan] || "#A1A1AA") + "55" }}>{plan || "—"}</span>;
  };

  const Th = ({ label, k }) => (
    <th className="text-left px-3 py-2 text-[10px] uppercase tracking-wider text-[#71717A]">
      {k ? (
        <button data-testid={`sort-${k}`} onClick={() => toggleSort(k)} className="flex items-center gap-1 hover:text-[#D4AF37]">
          {label} <ArrowUpDown className="w-3 h-3" />
        </button>
      ) : label}
    </th>
  );

  return (
    <div className="p-6">
      <header className="mb-5">
        <h2 className="font-serif-ax text-4xl text-[#F5F5F7] tracking-wide">Usuarios</h2>
        <p className="text-xs text-[#71717A] mt-0.5">Métricas en vivo · actualización cada 30s</p>
      </header>

      {(pendienteR || pendienteL) && (
        <div data-testid="usuarios-pendiente" className="mb-4 flex items-center gap-2 px-4 py-3 rounded-lg bg-[#F39C12]/10 border border-[#F39C12]/30 text-[#F39C12] text-sm">
          <Radio className="w-4 h-4" /> PUENTE PENDIENTE — algunos datos no están disponibles.
        </div>
      )}

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <StatCard icon={Users} label="Total usuarios" value={ahora.total} color="#D4AF37" testid="card-total" />
        <StatCard icon={Sparkles} label="En prueba (Trial)" value={ahora.trial} color="#4ECDC4" testid="card-trial" />
        <StatCard icon={Gift} label="Gratuitos (Free)" value={ahora.free} color="#A1A1AA" testid="card-free" />
        <StatCard icon={Crown} label="Suscritos (Premium)" value={ahora.premium} color="#FFD700" testid="card-premium" />
      </div>

      <div className="rounded-xl bg-[#0A0A0A] border border-[#1F1F24] p-5 mb-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-sm text-[#F5F5F7] uppercase tracking-wider">Crecimiento</h3>
          <span className="text-[10px] text-[#71717A] uppercase tracking-wider">Eje X: {modoMeses ? "Meses" : "Días"}</span>
        </div>
        <div className="flex gap-3 mb-3 flex-wrap">
          {SERIES.map((s) => (
            <button key={s.key} data-testid={`legend-${s.key}`} onClick={() => setHidden((h) => ({ ...h, [s.key]: !h[s.key] }))}
              className={`flex items-center gap-1.5 text-xs transition-opacity ${hidden[s.key] ? "opacity-30" : "opacity-100"}`}>
              <span className="w-3 h-1.5 rounded-full" style={{ backgroundColor: s.color }} /> {s.label}
            </button>
          ))}
        </div>
        <div style={{ width: "100%", height: 260 }}>
          <ResponsiveContainer>
            <LineChart data={chartData} margin={{ top: 5, right: 20, bottom: 5, left: -10 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#1F1F24" />
              <XAxis dataKey="x" stroke="#52525b" fontSize={11} />
              <YAxis stroke="#52525b" fontSize={11} allowDecimals={false} />
              <Tooltip contentStyle={{ background: "#0A0A0A", border: "1px solid #1F1F24", borderRadius: 8, color: "#F5F5F7" }} />
              {SERIES.filter((s) => !hidden[s.key]).map((s) => (
                <Line key={s.key} type="monotone" dataKey={s.key} stroke={s.color} strokeWidth={2} dot={false} name={s.label} />
              ))}
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="rounded-xl bg-[#0A0A0A] border border-[#1F1F24] overflow-hidden">
        <div className="p-4 flex items-center justify-between gap-3 border-b border-[#1F1F24] flex-wrap">
          <div className="flex items-center gap-2">
            <h3 className="text-sm text-[#F5F5F7] uppercase tracking-wider">Directorio</h3>
            {restringidos.length > 0 && (
              <span className="flex items-center gap-1 text-[10px] text-[#FF6B5B] bg-[#FF6B5B]/10 border border-[#FF6B5B]/30 rounded-full px-2 py-0.5">
                <ShieldAlert className="w-3 h-3" /> {restringidos.length} restringido(s)
              </span>
            )}
          </div>
          <div className="relative">
            <Search className="w-4 h-4 text-[#52525b] absolute left-3 top-1/2 -translate-y-1/2" />
            <Input data-testid="buscar-usuario" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar por email, plan o ID"
              className="pl-9 h-9 w-72 bg-[#050505] border-[#1F1F24] text-[#F5F5F7] focus-visible:ring-[#D4AF37]" />
          </div>
        </div>

        <div className="px-4 pb-3 flex items-center gap-3 text-[10px] text-[#71717A]">
          <span className="uppercase tracking-wider">Orden automático (en vivo): Premium → más combinaciones guardadas → más boletos guardados</span>
          {sortKey !== "prioridad" && (
            <button data-testid="orden-automatico" onClick={() => setSortKey("prioridad")} className="text-[#D4AF37] hover:underline">Reactivar orden automático</button>
          )}
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[#050505]">
              <tr>
                <Th label="Usuario" />
                <Th label="Plan" k="plan" />
                <Th label="Comb. guardadas" k="combinaciones_guardadas" />
                <Th label="Comb. generadas" k="combinaciones_total" />
                <Th label="Boletos guardados" k="boletos_total" />
                <Th label="Uso/día (prom. · hoy)" k="uso_min" />
                <Th label="Estado" />
                <Th label="" />
              </tr>
            </thead>
            <tbody>
              {/* GUARDIÁN ANTI-ABUSO: restringidos siempre arriba, fila roja titilante */}
              {restringidos.map((u) => (
                <tr key={u.user_id} data-testid={`row-restringido-${u.user_id}`} className="ax-blink-row border-t border-[#FF6B5B]/20">
                  <td className="px-3 py-2.5 text-[#F5F5F7] font-mono-ax text-xs">{u.email || u.telefono || u.user_id.slice(0, 8)}</td>
                  <td className="px-3 py-2.5"><PlanBadge plan={u.plan} /></td>
                  <td className="px-3 py-2.5 font-mono-ax text-xs text-[#FFD700]">{fmtNum(u.combinaciones_guardadas)}</td>
                  <td className="px-3 py-2.5 font-mono-ax text-xs text-[#F5F5F7]">{fmtNum(u.combinaciones_hoy)} / {fmtNum(u.combinaciones_total)}</td>
                  <td className="px-3 py-2.5 font-mono-ax text-xs text-[#F5F5F7]">{fmtNum(u.boletos_hoy)} / {fmtNum(u.boletos_total)}</td>
                  <td className="px-3 py-2.5 font-mono-ax text-xs text-[#A1A1AA]">{fmtDuracion(usoMin(u))}<span className="text-[#52525b]"> · {fmtDuracion(usoHoyMin(u))}</span></td>
                  <td className="px-3 py-2.5"><span className="text-[10px] text-[#FF6B5B] uppercase tracking-wider">Restringido</span></td>
                  <td className="px-3 py-2.5 text-right">
                    <button data-testid={`abrir-expediente-${u.user_id}`} onClick={() => abrirExpediente(u)}
                      className="text-[10px] uppercase tracking-wider px-3 py-1.5 rounded-md bg-[#FF6B5B]/15 text-[#FF6B5B] border border-[#FF6B5B]/40 hover:bg-[#FF6B5B]/25">
                      Abrir expediente
                    </button>
                  </td>
                </tr>
              ))}
              {filtrados.map((u) => (
                <tr key={u.user_id} data-testid={`row-usuario-${u.user_id}`} className="border-t border-[#1F1F24] hover:bg-white/[0.02]">
                  <td className="px-3 py-2.5 text-[#F5F5F7] font-mono-ax text-xs">{u.email || u.telefono || u.user_id.slice(0, 8)}</td>
                  <td className="px-3 py-2.5"><PlanBadge plan={u.plan} /></td>
                  <td className="px-3 py-2.5 font-mono-ax text-xs text-[#FFD700]">{fmtNum(u.combinaciones_guardadas)}</td>
                  <td className="px-3 py-2.5 font-mono-ax text-xs text-[#F5F5F7]">{fmtNum(u.combinaciones_hoy)} / {fmtNum(u.combinaciones_total)}</td>
                  <td className="px-3 py-2.5 font-mono-ax text-xs text-[#F5F5F7]">{fmtNum(u.boletos_hoy)} / {fmtNum(u.boletos_total)}</td>
                  <td className="px-3 py-2.5 font-mono-ax text-xs text-[#A1A1AA]">{fmtDuracion(usoMin(u))}<span className="text-[#52525b]"> · {fmtDuracion(usoHoyMin(u))}</span></td>
                  <td className="px-3 py-2.5"><span className="text-[10px] text-[#4ECDC4] uppercase tracking-wider">Activo</span></td>
                  <td className="px-3 py-2.5" />
                </tr>
              ))}
              {!usuarios.length && (
                <tr><td colSpan={8} className="px-3 py-10 text-center text-[#52525b] text-sm">Sin usuarios para mostrar.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <Dialog open={!!expUser} onOpenChange={(o) => !o && setExpUser(null)}>
        <DialogContent className="bg-[#0A0A0A] border border-[#1F1F24] text-[#F5F5F7] max-w-lg" data-testid="expediente-usuario">
          <DialogHeader>
            <DialogTitle className="font-serif-ax text-2xl text-[#FF6B5B] flex items-center gap-2">
              <ShieldAlert className="w-5 h-5" /> Expediente del usuario
            </DialogTitle>
          </DialogHeader>
          {expUser && (
            <div className="space-y-4 text-sm mt-2">
              <div className="font-mono-ax text-xs text-[#A1A1AA]">{expUser.email || expUser.user_id}</div>
              {expUser.restriccion && (
                <div className="rounded-lg bg-[#FF6B5B]/10 border border-[#FF6B5B]/25 p-4 space-y-1.5">
                  <p className="text-xs text-[#FF6B5B] uppercase tracking-wider mb-1">Motivo de la restricción</p>
                  <p className="text-[#F5F5F7]">{expUser.restriccion.motivo || "—"}</p>
                  <div className="flex justify-between text-xs text-[#A1A1AA] pt-2">
                    <span>Desde: {fmtFecha(expUser.restriccion.desde)}</span>
                    <span>Hasta: {fmtFecha(expUser.restriccion.hasta)}</span>
                  </div>
                </div>
              )}
              <div className="grid grid-cols-2 gap-3">
                <MetricBox label="Combinaciones hoy / total" value={`${fmtNum(expUser.combinaciones_hoy)} / ${fmtNum(expUser.combinaciones_total)}`} />
                <MetricBox label="Boletos hoy / total" value={`${fmtNum(expUser.boletos_hoy)} / ${fmtNum(expUser.boletos_total)}`} />
              </div>

              {expLoading ? (
                <div className="flex items-center gap-2 text-[#71717A] text-xs"><Loader2 className="w-4 h-4 animate-spin" /> Cargando expediente completo…</div>
              ) : expData?.puente_pendiente ? (
                <p className="text-[#F39C12] text-xs">PUENTE PENDIENTE — detalle completo no disponible.</p>
              ) : null}

              <div className="flex gap-3 pt-2">
                <button data-testid="btn-desbloquear" disabled={acting} onClick={() => accion("desbloquear")}
                  className="flex-1 h-11 rounded-lg bg-[#4ECDC4]/15 text-[#4ECDC4] border border-[#4ECDC4]/40 hover:bg-[#4ECDC4]/25 flex items-center justify-center gap-2 disabled:opacity-50">
                  <Unlock className="w-4 h-4" /> Desbloquear
                </button>
                <button data-testid="btn-mantener-bloqueo" disabled={acting} onClick={() => accion("bloquear")}
                  className="flex-1 h-11 rounded-lg bg-[#FF6B5B]/15 text-[#FF6B5B] border border-[#FF6B5B]/40 hover:bg-[#FF6B5B]/25 flex items-center justify-center gap-2 disabled:opacity-50">
                  <Lock className="w-4 h-4" /> Mantener bloqueo
                </button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

const MetricBox = ({ label, value }) => (
  <div className="rounded-lg bg-[#050505] border border-[#1F1F24] p-3">
    <p className="text-[10px] text-[#71717A] uppercase tracking-wider mb-1">{label}</p>
    <p className="font-mono-ax text-[#F5F5F7]">{value}</p>
  </div>
);

export default Usuarios;
